import React from 'react';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ArrowRight,
  Building2,
  ClipboardCheck,
  GraduationCap,
  HeartPulse,
  Quote,
  ShieldCheck,
} from 'lucide-react';
import { marketingLogos, marketingPalette } from '../content/marketing';

const headingFontFamily = '"Sora", "Manrope", sans-serif';
const bodyFontFamily = '"Manrope", "Segoe UI", sans-serif';
const palette = marketingPalette;

const principles = [
  {
    icon: HeartPulse,
    title: 'Realistic practice',
    copy:
      'Virtual Medic is built inside a game engine so the consultation feels more like a patient interaction and less like another chatbot window.',
  },
  {
    icon: ClipboardCheck,
    title: 'Useful feedback',
    copy:
      'The aim is not just to let students practise more often. It is to show them what improved, what was missed, and what to focus on next.',
  },
  {
    icon: Building2,
    title: 'Academic control',
    copy:
      'Universities need validated content, mark schemes, and analytics they can trust. The platform is designed around that reality from the start.',
  },
];

const audienceFocus = [
  {
    title: 'Students',
    points: [
      'A repeatable place to practise consultations without pressure.',
      'A clearer bridge between passive revision and real patient encounters.',
      'A more focused way to improve before exams, placements, and OSCEs.',
    ],
  },
  {
    title: 'Medical schools',
    points: [
      'A modular case system that fits local teaching and assessment standards.',
      'Institution-owned content, cases, and data rather than generic external libraries.',
      'Analytics that can help staff identify patterns across students and cohorts.',
    ],
  },
];

function AboutPage() {
  const isAuthenticated = Boolean(localStorage.getItem('token'));

  return (
    <Box sx={{ bgcolor: palette.heroBlueMist, color: palette.ink, fontFamily: bodyFontFamily }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background:
            `radial-gradient(circle at top right, ${alpha(palette.heroBlue, 0.24)}, transparent 32%), linear-gradient(180deg, #eef8ff 0%, #ffffff 64%, #edf8fe 100%)`,
          pt: { xs: 5, md: 7 },
          pb: { xs: 7, md: 9 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: { xs: '28px', md: '36px' },
                  p: { xs: 3, sm: 4, md: 5 },
                  bgcolor: alpha('#ffffff', 0.86),
                  border: `1px solid ${alpha(palette.ink, 0.08)}`,
                  boxShadow: '0 26px 70px rgba(18, 61, 103, 0.08)',
                }}
              >
                <Box
                  component="img"
                  src={marketingLogos.lockupDark}
                  alt="Virtual Medic"
                  sx={{
                    width: { xs: '100%', sm: 360 },
                    maxWidth: '100%',
                    mb: 3,
                  }}
                />
                <Typography
                  sx={{
                    mb: 1.25,
                    color: palette.heroBlueMid,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: '0.82rem',
                  }}
                >
                  About Virtual Medic
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    mb: 2.25,
                    fontFamily: headingFontFamily,
                    fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.35rem' },
                    lineHeight: 1.06,
                    letterSpacing: '-0.03em',
                    maxWidth: 720,
                  }}
                >
                  Built to close the feedback gap in consultation practice.
                </Typography>
                <Typography
                  sx={{
                    mb: 3,
                    maxWidth: 700,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.78,
                    color: palette.inkSoft,
                  }}
                >
                  Virtual Medic began with a straightforward problem: students can often find ways to
                  practise, but they still struggle to get detailed, consistent feedback on a full
                  consultation. The platform is designed to make independent rehearsal more realistic,
                  more repeatable, and more useful.
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 700,
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.78,
                    color: palette.inkSoft,
                  }}
                >
                  It is not intended to replace teaching on wards or real patient contact. It is meant
                  to support the transition between theory, peer practice, and clinical placements by
                  giving learners a better place to prepare.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                  <Button
                    component={Link}
                    to={isAuthenticated ? '/dashboard' : '/signup'}
                    endIcon={<ArrowRight size={18} />}
                    variant="contained"
                    sx={{
                      minHeight: 52,
                      px: 3,
                      bgcolor: palette.heroBlueDeep,
                      color: palette.white,
                      textTransform: 'none',
                      fontWeight: 800,
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: palette.heroBlueDeeper,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isAuthenticated ? 'Open dashboard' : 'Get started'}
                  </Button>
                  <Button
                    component={Link}
                    to="/downloads"
                    variant="outlined"
                    sx={{
                      minHeight: 52,
                      px: 3,
                      color: palette.heroBlueDeep,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: bodyFontFamily,
                      borderRadius: '999px',
                      borderColor: alpha(palette.heroBlueDeep, 0.18),
                      '&:hover': {
                        borderColor: alpha(palette.heroBlueDeep, 0.36),
                        bgcolor: alpha(palette.heroBlueDeep, 0.03),
                      },
                    }}
                  >
                    Download app
                  </Button>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: { xs: '28px', md: '36px' },
                  p: { xs: 3, sm: 4, md: 4.5 },
                  background: 'linear-gradient(160deg, #123d67 0%, #1c6da1 100%)',
                  color: palette.white,
                  boxShadow: '0 28px 80px rgba(18, 61, 103, 0.16)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '14px',
                      bgcolor: alpha('#ffffff', 0.12),
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Quote size={22} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.04em' }}>
                    Founder note
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    mb: 2,
                    fontFamily: headingFontFamily,
                    fontSize: { xs: '1.65rem', md: '2rem' },
                    lineHeight: 1.15,
                  }}
                >
                  Built by a medical student who wanted a better way to practise.
                </Typography>

                <Typography sx={{ mb: 2, color: alpha('#f9fcfb', 0.82), lineHeight: 1.78 }}>
                  Virtual Medic was started by Abhay Shivamurthy during medical school to create a
                  more consistent way to practise for exams. Practising with friends and family was
                  possible, but it rarely delivered the quality of feedback needed to know whether
                  performance was actually improving.
                </Typography>

                <Typography sx={{ mb: 3, color: alpha('#f9fcfb', 0.82), lineHeight: 1.78 }}>
                  That experience shaped the product: realistic patient interaction, structured
                  feedback, and a system that can work for both individual learners and universities.
                </Typography>

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: '24px',
                    bgcolor: alpha('#ffffff', 0.08),
                    border: `1px solid ${alpha('#ffffff', 0.10)}`,
                  }}
                >
                  <Typography
                    sx={{
                      mb: 1,
                      color: palette.heroBlueSoft,
                      fontWeight: 800,
                      fontSize: '0.8rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    One-line summary
                  </Typography>
                  <Typography sx={{ lineHeight: 1.75, color: alpha('#f9fcfb', 0.86) }}>
                    Virtual Medic gives medical students unlimited consultation practice with automated,
                    exam-level feedback.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 }, background: '#ffffff' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ maxWidth: 760, mb: 4.5 }}>
            <Typography
              sx={{
                mb: 1.25,
                color: palette.heroBlueMid,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.82rem',
              }}
            >
              What shapes the product
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontFamily: headingFontFamily,
                fontSize: { xs: '2rem', sm: '2.45rem', md: '3rem' },
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
              }}
            >
              Realism, feedback, and control.
            </Typography>
            <Typography
              sx={{
                maxWidth: 700,
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.75,
                color: palette.inkSoft,
              }}
            >
              Those three ideas keep showing up throughout the platform because they are the parts that
              matter most in real training environments.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {principles.map((principle) => {
              const Icon = principle.icon;

              return (
                <Grid item xs={12} md={4} key={principle.title}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: '28px',
                      p: { xs: 3, md: 3.5 },
                      border: `1px solid ${alpha(palette.ink, 0.08)}`,
                      background: 'linear-gradient(180deg, #ffffff 0%, #f6fbff 100%)',
                      boxShadow: '0 18px 48px rgba(18, 61, 103, 0.05)',
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 54,
                        height: 54,
                        borderRadius: '18px',
                        bgcolor: alpha(palette.heroBlue, 0.14),
                        color: palette.heroBlueMid,
                      }}
                    >
                      <Icon size={26} strokeWidth={1.9} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        mb: 1.5,
                        fontFamily: headingFontFamily,
                        fontSize: '1.45rem',
                        lineHeight: 1.18,
                      }}
                    >
                      {principle.title}
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72 }}>
                      {principle.copy}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 }, background: '#eef8fe' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '30px',
                  p: { xs: 3, md: 4 },
                  background: 'linear-gradient(155deg, #123d67 0%, #1c6da1 100%)',
                  color: palette.white,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '16px',
                      bgcolor: alpha('#ffffff', 0.12),
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <GraduationCap size={24} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, letterSpacing: '0.04em' }}>
                    Why it matters
                  </Typography>
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    mb: 2,
                    fontFamily: headingFontFamily,
                    fontSize: { xs: '1.9rem', md: '2.3rem' },
                    lineHeight: 1.12,
                  }}
                >
                  Real learning still happens on wards. This is the bridge.
                </Typography>

                <Typography sx={{ color: alpha('#f9fcfb', 0.84), lineHeight: 1.78 }}>
                  Virtual Medic is designed to support the move from theory into practice. It helps
                  students build confidence, strengthen structure, and arrive at real patient encounters
                  better prepared to listen, think, and communicate clearly.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Grid container spacing={3}>
                {audienceFocus.map((group) => (
                  <Grid item xs={12} md={6} key={group.title}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: '28px',
                        p: { xs: 3, md: 3.5 },
                        bgcolor: '#ffffff',
                        border: `1px solid ${alpha(palette.ink, 0.08)}`,
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          mb: 2,
                          fontFamily: headingFontFamily,
                          fontSize: '1.5rem',
                          lineHeight: 1.16,
                        }}
                      >
                        {group.title}
                      </Typography>

                      <Stack component="ul" spacing={1.4} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                        {group.points.map((point) => (
                          <Stack
                            key={point}
                            component="li"
                            direction="row"
                            spacing={1.25}
                            alignItems="flex-start"
                          >
                            <ShieldCheck size={18} color={palette.heroBlueMid} style={{ marginTop: 3, flexShrink: 0 }} />
                            <Typography sx={{ color: palette.inkSoft, lineHeight: 1.72 }}>{point}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, background: '#f3faff' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              borderRadius: { xs: '28px', md: '36px' },
              p: { xs: 3, sm: 4, md: 5 },
              bgcolor: '#ffffff',
              border: `1px solid ${alpha(palette.ink, 0.08)}`,
              boxShadow: '0 22px 56px rgba(18, 61, 103, 0.06)',
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            <Typography
              sx={{
                mb: 1.25,
                color: palette.heroBlueMid,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.82rem',
              }}
            >
              Next step
            </Typography>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontFamily: headingFontFamily,
                fontSize: { xs: '2rem', sm: '2.35rem', md: '2.8rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
              }}
            >
              Explore the product or start practising.
            </Typography>
            <Typography
              sx={{
                maxWidth: 620,
                mx: { md: 'auto' },
                mb: 3.5,
                color: palette.inkSoft,
                lineHeight: 1.75,
              }}
            >
              The homepage shows the product at a glance. If you are ready to use it, head straight
              into the platform or download the app.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent={{ md: 'center' }}
            >
              <Button
                component={Link}
                to="/"
                variant="outlined"
                sx={{
                  minHeight: 52,
                  px: 3,
                  color: palette.heroBlueDeep,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontFamily: bodyFontFamily,
                  borderRadius: '999px',
                  borderColor: alpha(palette.heroBlueDeep, 0.18),
                  '&:hover': {
                    borderColor: alpha(palette.heroBlueDeep, 0.36),
                    bgcolor: alpha(palette.heroBlueDeep, 0.03),
                  },
                }}
              >
                Back to homepage
              </Button>
              <Button
                component={Link}
                to={isAuthenticated ? '/dashboard' : '/signup'}
                endIcon={<ArrowRight size={18} />}
                variant="contained"
                sx={{
                  minHeight: 52,
                  px: 3,
                  bgcolor: palette.heroBlueDeep,
                  color: palette.white,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontFamily: bodyFontFamily,
                  borderRadius: '999px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: palette.heroBlueDeeper,
                    boxShadow: 'none',
                  },
                }}
              >
                {isAuthenticated ? 'Open dashboard' : 'Create an account'}
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default AboutPage;
